import { Box, CircularProgress, Modal } from "@mui/material";
import React, { useCallback, useContext, useEffect, useMemo } from 'react';
import { ReconContext } from "../recon-context";
import { RECON_DATA, RECON_LOGS_TABLE_COLUMNS, ReconLogsColumnKeys, recon_logs_default_limit } from "../constants";
import { ReconLogEntryType } from "../recon-context/types";
import CommonPagination from "../../../../components/reusable-ui/common-pagination";
import { CrossIcon } from "../../../../components/reusable-ui/basic-modal";
type ReportType = {
  fileName: string;
  reportId: string;
};
export default function ReconLogsModal() {
  const { toggleReconLogs } = useContext(ReconContext);

  return <Modal className='font-inter' open={true} onClose={toggleReconLogs}>
    <Box className={`modal-sheet w-[987px] flex top-[85px] pt-[21px] pl-[21px] pr-[12px] pb-[12px]`}>
      <CrossIcon handleClick={toggleReconLogs} className="absolute left-[-68px] top-[4px]" />
      <div className="font-semibold text-20 text-[#181D27]">{RECON_DATA.reconLogsModal.header}</div>
      <ReconLogsTable />
    </Box>
  </Modal>
}

function NoLogsPresent() {
  return <div className="flex flex-col items-center justify-center h-full">
    <div className="rista-sprite no-payout-files"></div>
    <div className="font-semibold text-20 text-black/70 mt-[32px]">{RECON_DATA.reconLogsModal.noLogsHeader}</div>
    <div className="text-17 text-black/40 mt-[11px]">{RECON_DATA.reconLogsModal.noLogsDescription}</div>
  </div>
}

function ReconLogsTable() {
  const { reconContextData: { reconLogsModal: { data: { totalFiles }, loading } }, fetchReconLogs } = useContext(ReconContext);
  return <div className="flex flex-col border border-[#E6E6E6] rounded-[8px] mt-[21px] flex-1 overflow-auto">
    {totalFiles ? <table className="text-13 font-normal">
      <ReconLogsHeader />
      {loading ? <CircularProgress sx={{ width: "80px", height: "80px" }} /> : <ReconLogsBody />}
    </table> : <NoLogsPresent />}
    {totalFiles && <div className="flex justify-end mt-auto absolute bottom-[14px] bg-white w-fit right-14">
      <CommonPagination getUpdatedRecords={fetchReconLogs} totalCount={totalFiles} limit={recon_logs_default_limit} />
    </div>}
  </div>
}

function ReconLogsHeader() {
  const widths = [25, 25, 10, 40]
  return <thead className="sticky top-0 z-10">
    {(Object.keys(RECON_LOGS_TABLE_COLUMNS) as ReconLogsColumnKeys[]).map((colKey: ReconLogsColumnKeys, index: number) => {
      return <td key={colKey} className={`bg-greyF6 text-grey7F h-[53px] text-12 font-medium p-[12px] w-[${widths[index]}%]`
      } >
        {RECON_LOGS_TABLE_COLUMNS[colKey]}
      </td>
    })}
  </thead>
}

function ReconLogsBody() {
  const { reconContextData: { reconLogsModal: { data: { files } } } } = useContext(ReconContext);
  const getIcon = useCallback((log: ReconLogEntryType) => {
    switch (log.reconStatus) {
      case 'COMPLETED': return 'success-check-icon';
      case 'FAILED': return 'error-icon';
      case "PENDING": return '';
    }
  }, []);

  const getStatusText = useCallback((log: ReconLogEntryType) => {
    switch (log.reconStatus) {
      case 'COMPLETED': return 'Successfully Reconciled';
      case 'FAILED': return log?.eventLog?.[log.eventLog?.length - 1]?.reason || 'Failed';
      case "PENDING": return 'In Progress';
    }
  }, []);

  function renderLogsRow(log: ReconLogEntryType) {
    const isPending = log.reconStatus === 'PENDING';
    const icon = getIcon(log);
    const statusText = getStatusText(log);
    function renderStatus() {
      return <div className="flex items-center gap-12">
        {icon ? <div className={`rista-sprite ${icon}`}></div> : <CircularProgress className="h-[21px] w-[21px]" />}
        <div>{statusText}</div>
      </div>
    }

    const createdDate = new Date(log.createdDate || '');
    const formattedDate = createdDate.toLocaleString('en-US', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      hour12: true
    }).replace(',', '');
    return (
      <tr className={`h-[48px] text-13 border-b border-[greyDEDEDF] ${isPending ? 'text-black/60' : 'text-black/[87%]'} `} key={log.createdDate}>
        <td className=" p-[12px]" >{formattedDate}</td>
        <td title={log?.attachment?.fileName} className="p-[12px] max-w-[300px] w-[300px]">
          <div className="line-clamp-2 overflow-hidden text-ellipsis block">
            {log.attachment?.fileName}
          </div>
        </td>
        <td className=" p-[12px]" >{log.attachment.eventBy?.userName || ''}</td>
        <td className=" p-[12px]" >{renderStatus()}</td>
      </tr>
    );
  }

  return <tbody className="overflow-scroll">{files.map(renderLogsRow)}</tbody>;
}
